import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import {
  SupportMessageDialog,
  SupportMessagesFilters,
  SupportMessagesTable,
  supportMessageModel,
} from 'entities/support-message';

import { ESupportMessageStatus } from 'shared/api';
import { useDebouncedSearch } from 'shared/lib';
import { LoadingProgress } from 'shared/ui';

export const SupportMessages = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filter, setFilter] = useState(searchParams.get('filter') ?? '');
  const [subject, setSubject] = useState(searchParams.get('subject') ?? '');
  const [language, setLanguage] = useState(searchParams.get('language') ?? '');
  const [status, setStatus] = useState<ESupportMessageStatus | ''>(
    (searchParams.get('status') as ESupportMessageStatus) ?? '',
  );
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') ?? 'createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    (searchParams.get('sortOrder') as 'asc' | 'desc') ?? 'desc',
  );
  const [page, setPage] = useState(parseInt(searchParams.get('page') ?? '1', 10));
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(searchParams.get('limit') ?? '20', 10));

  // Dialog state
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Mutations
  const { mutate: reply, isLoading: isReplying } = supportMessageModel.replySupportMessageAsync();

  const debouncedFilter = useDebouncedSearch(filter, 400);

  const params = useMemo(
    () => ({
      filter: debouncedFilter || undefined,
      sortBy,
      sortOrder,
      page,
      limit: rowsPerPage,
      status: status || undefined,
      subject: subject || undefined,
      language: language || undefined,
    }),
    [debouncedFilter, subject, sortBy, sortOrder, page, rowsPerPage, status, language],
  );

  const { isLoading, isFetching } = supportMessageModel.getSupportMessagesAsync(params)(dispatch);
  const { messageList, pagination } = supportMessageModel.useSupportMessageList();

  // Fetch selected message details
  const { data: messageData, isLoading: isMessageLoading } = supportMessageModel.getSupportMessageByIdAsync(
    selectedMessageId ?? '',
    !!selectedMessageId && isDialogOpen,
  );

  // Update URL params
  useEffect(() => {
    const newParams: Record<string, string> = {
      page: page.toString(),
      limit: rowsPerPage.toString(),
      sortBy,
      sortOrder,
    };

    if (debouncedFilter) newParams.filter = debouncedFilter;
    if (subject) newParams.subject = subject;
    if (status) newParams.status = status;
    if (language) newParams.language = language;

    setSearchParams(newParams, { replace: true });
  }, [debouncedFilter, subject, status, sortBy, sortOrder, page, rowsPerPage, language, setSearchParams]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((value: string) => {
    setFilter(value);
    setPage(1);
  }, []);

  const handleSubjectChange = useCallback((value: string) => {
    setSubject(value);
    setPage(1);
  }, []);

  const handleLanguageChange = useCallback((value: string) => {
    setLanguage(value);
    setPage(1);
  }, []);

  const handleStatusChange = useCallback((value: ESupportMessageStatus | '') => {
    setStatus(value);
    setPage(1);
  }, []);

  const handleSortChange = useCallback(
    (columnId: string) => {
      if (sortBy === columnId) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(columnId);
        setSortOrder('asc');
      }
      setPage(1);
    },
    [sortBy, sortOrder],
  );

  const handleRowClick = useCallback((id: string) => {
    setSelectedMessageId(id);
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setSelectedMessageId(null);
  }, []);

  const handleReply = useCallback(
    (replyText: string) => {
      if (selectedMessageId) {
        reply(
          { id: selectedMessageId, replyText },
          {
            onSuccess: () => {
              setIsDialogOpen(false);
              setSelectedMessageId(null);
            },
          },
        );
      }
    },
    [selectedMessageId, reply],
  );

  if (isLoading && !messageList.length) return <LoadingProgress />;

  return (
    <Box display="flex" flexDirection="column" flex={1} px={3} pt={2}>
      <SupportMessagesFilters
        filter={filter}
        status={status}
        subject={subject}
        language={language}
        totalCount={pagination?.total ?? 0}
        onFilterChange={handleFilterChange}
        onStatusChange={handleStatusChange}
        onSubjectChange={handleSubjectChange}
        onLanguageChange={handleLanguageChange}
      />

      <SupportMessagesTable
        list={messageList || []}
        pagination={pagination}
        page={page}
        rowsPerPage={rowsPerPage}
        sortBy={sortBy}
        sortOrder={sortOrder}
        isLoading={isFetching}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onSortChange={handleSortChange}
        onRowClick={handleRowClick}
      />

      <SupportMessageDialog
        message={messageData?.data ?? null}
        isOpen={isDialogOpen}
        isLoading={isMessageLoading}
        isReplying={isReplying}
        onClose={handleCloseDialog}
        onReply={handleReply}
      />
    </Box>
  );
};
